import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useState, useRef } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { SidebarHeaderMenu } from "@/components/SidebarHeaderMenu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarSeparator,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Plus } from "lucide-react";
import { CreateSiteDialog } from "@/components/CreateSiteDialog";
import { useSitesWithKPIs } from "@/hooks/useSitesWithKPIs";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/useMobile";
import { COMPANY_SIDEBAR_NAV } from "@/config/navigation";
import type { SidebarHeaderMenuVariant } from "@/components/SidebarHeaderMenu";

const HealthIndicator = ({
  status,
}: {
  status: "ok" | "warning" | "problem";
}) => {
  const colors = {
    ok: "bg-success",
    warning: "bg-warning",
    problem: "bg-destructive",
  };

  return (
    <div className="w-4 h-4 flex items-center justify-center shrink-0">
      <div className={`w-2 h-2 rounded-full ${colors[status]}`} />
    </div>
  );
};

export function AppSidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { sites, loading, refetch } = useSitesWithKPIs();
  const { unreadCount } = useUnreadNotifications();
  const isMobile = useIsMobile();
  const hasAnimated = useRef(false);

  const navigationItems = COMPANY_SIDEBAR_NAV.filter((item) => {
    if (item.restrictedTo) return item.restrictedTo.includes(user?.role || "");
    return true;
  });

  const activeSites = sites
    .filter(
      (site) => site.status === "in_progress" || site.status === "planning",
    )
    .slice(0, 8);

  const getInitialAnimation = () => {
    if (hasAnimated.current) return false;
    return { opacity: 0, x: -12 };
  };

  return (
    <Sidebar collapsible={isMobile ? "offcanvas" : "none"} variant="sidebar" className="border-none h-screen">
      <SidebarHeader className="bg-sidebar py-sm px-sm">
        <SidebarHeaderMenu variant={(user?.role as SidebarHeaderMenuVariant) || 'company_director'} />
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent className="bg-sidebar overflow-y-auto overflow-x-hidden">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item, index) => {
                const isActive =
                  location.pathname === item.url ||
                  location.pathname.startsWith(item.url + "/");
                const showBadge =
                  item.title === "Notifications" && unreadCount > 0;

                return (
                  <motion.div
                    key={item.title}
                    initial={getInitialAnimation()}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.2,
                      delay: hasAnimated.current ? 0 : index * 0.03,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    onAnimationComplete={() => {
                      if (index === navigationItems.length - 1) {
                        hasAnimated.current = true;
                      }
                    }}
                  >
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        className={cn(
                          "text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors",
                          isActive && "bg-accent text-foreground",
                        )}
                      >
                        <NavLink
                          to={item.url}
                          className="flex items-center justify-start gap-sm"
                        >
                          <item.icon
                            className="w-4 h-4 shrink-0"
                            strokeWidth={1.8}
                          />
                          <span className="flex-1 truncate text-body-md">
                            {item.title}
                          </span>
                          {showBadge && (
                            <Badge
                              variant="destructive"
                              className="h-5 min-w-5 flex items-center justify-center text-label-sm px-1.5"
                            >
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </Badge>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {item.url === "/sites" &&
                      !loading &&
                      activeSites.map((site) => {
                        const siteSlug = site.short_code ?? site.id;
                        const isSiteActive = location.pathname.startsWith(
                          `/sites/${siteSlug}`,
                        );
                        return (
                          <SidebarMenuItem key={site.id} className="pl-md">
                            <SidebarMenuButton
                              asChild
                              isActive={isSiteActive}
                              className={cn(
                                "text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors",
                                isSiteActive && "bg-accent text-foreground",
                              )}
                            >
                              <NavLink
                                to={`/sites/${siteSlug}`}
                                className="flex items-center justify-start gap-sm"
                              >
                                <HealthIndicator status={site.health_status} />
                                <span className="truncate text-body-md">{site.name}</span>
                              </NavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                  </motion.div>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="bg-sidebar px-md pt-md pb-xl gap-1">
        <button
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center justify-center gap-sm w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-body-md font-medium"
        >
          <Plus className="w-4 h-4 shrink-0" strokeWidth={2} />
          Nouveau chantier
        </button>
      </SidebarFooter>

      <CreateSiteDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={() => {
          refetch();
          setIsCreateDialogOpen(false);
        }}
      />
    </Sidebar>
  );
}
